<template>
    <div class="camp-programs-page">
        <div class="programs-hero">
            <div class="programs-hero-overlay"></div>
            <b-container class="programs-hero-content">
                <h1>{{ seriesName || '營隊方案' }}</h1>
                <p>依課程類型與報名狀態篩選，選擇想了解的營隊方案。</p>
            </b-container>
        </div>

        <b-container class="py-5">
            <div class="section-heading">
                <div>
                 
                    <h2>營隊方案列表</h2>
                    <div
                        v-if="hasSeriesDescription"
                        class="series-description"
                        v-html="seriesDescription"
                    ></div>
                </div>
                <b-spinner v-if="loading.programs" small variant="primary"></b-spinner>
            </div>

            <div class="camp-filter-bar">
                <b-form-group label="課程類型" label-class="camp-filter-label">
                    <b-form-radio-group
                        class="camp-filter-options"
                        v-model="filters.seasonType"
                        :options="seasonTypeOptions"
                        buttons
                        button-variant="outline-primary"
                        size="sm"
                    ></b-form-radio-group>
                </b-form-group>
                <b-form-group label="適合年級" label-class="camp-filter-label">
                    <b-form-radio-group
                        class="camp-filter-options"
                        v-model="filters.grade"
                        :options="gradeOptions"
                        buttons
                        button-variant="outline-primary"
                        size="sm"
                    ></b-form-radio-group>
                </b-form-group>
            </div>

            <template v-if="loading.programs">
                <div class="loading-block">
                    <b-spinner variant="primary"></b-spinner>
                </div>
            </template>

            <b-alert v-else-if="programList.length === 0" show variant="light" class="text-center">
                目前沒有符合條件的營隊方案。
            </b-alert>

            <b-row v-else>
                <b-col
                    v-for="program in programList"
                    :key="program.Id"
                    cols="12"
                    lg="6"
                    class="mb-4"
                >
                    <b-card no-body class="camp-program-card" @click="goProgram(program)">
                        <b-row no-gutters class="camp-program-layout">
                            <b-col cols="12" md="5">
                                <figure class="camp-program-cover mb-0">
                                    <b-card-img
                                        :src="getProgramCover(program)"
                                        :alt="program.Name"
                                        @error="setDefaultCover"
                                    ></b-card-img>
                                </figure>
                            </b-col>
                            <b-col cols="12" md="7">
                                <b-card-body>
                                    <b-badge :variant="program.DisplayCanApply ? 'success' : 'secondary'" class="camp-apply-status mb-2">
                                        {{ program.DisplayCanApply ? '招生中' : '規劃中' }}
                                    </b-badge>
                                    <div class="d-flex align-items-start justify-content-between mb-2">
                                        <h3 class="program-title" :title="program.Name">{{ program.Name }}</h3>
                                        <b-badge v-if="program.Mark" variant="warning" class="ml-2 text-nowrap">
                                            精選
                                        </b-badge>
                                    </div>
                                    <div class="camp-meta-row">
                                        <b-badge v-if="program.Year" variant="light" class="mr-2">
                                            {{ program.Year }}
                                        </b-badge>
                                        <b-badge v-if="program.SeasonType" variant="light" class="mr-2">
                                            {{ seasonText(program.SeasonType) }}
                                        </b-badge>
                                        <b-badge v-if="program.GradeMin || program.GradeMax" variant="light">
                                            <template v-if="program.GradeMin && program.GradeMax">
                                                {{ program.GradeMin }} - {{ program.GradeMax }} 年級
                                            </template>
                                            <template v-else-if="program.GradeMin">
                                                {{ program.GradeMin }} 年級以上
                                            </template>
                                            <template v-else>
                                                {{ program.GradeMax }} 年級以下
                                            </template>
                                        </b-badge>
                                    </div>
                                    <div v-if="hasProgramPrice(program)" class="program-price-slot">
                                        <div
                                            :class="isProgramEarlyBirdActive(program) ? 'program-price early-bird' : 'program-price'"
                                        >
                                            <span v-if="isProgramEarlyBirdActive(program)" class="program-price-label">早鳥優惠</span>
                                            NT${{ money(programDisplayPrice(program)) }}
                                            <span
                                                v-if="isProgramEarlyBirdActive(program)"
                                                class="program-price-original"
                                            >
                                                售價 NT${{ money(program.DefaultProductPrice) }}
                                            </span>
                                            <span
                                                v-if="isProgramEarlyBirdActive(program)"
                                                class="program-price-deadline"
                                            >
                                                截止 {{ formatDate(program.DefaultEarlyBirdEnd) }}
                                            </span>
                                        </div>
                                    </div>
                                </b-card-body>
                            </b-col>
                        </b-row>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>
        <div class="fixed-bottom-actions">
            <b-button variant="outline-secondary" class="back-button" @click="_go('./camps')">
                返回營隊系列
            </b-button>
        </div>
    </div>
</template>

<script>
import { apiGetCampSeriesProgramList, apiGetCampSeasonTypeSelector } from '@/api/API.js';
import { getCampSeasonText, normalizeCampSeasonType } from '@/utils/campEnums.js';

export default {
    name: 'CampPrograms',
    data() {
        return {
            id: '',
            series: null,
            programList: [],
            allProgramList: [],
            Param: {
                Paging: {
                    pageNumber: 1,
                    pageSize: 999,
                },
                Search: {},
                Sort: {},
            },
            filters: {
                seasonType: '',
                grade: '',
            },
            loading: {
                programs: false,
            },
            seasonOptions: [],
        };
    },
    computed: {
        seriesName() {
            return this.series ? this.series.Name : '';
        },
        hasSeriesDescription() {
            return !!(this.series && this.series.Description);
        },
        seriesDescription() {
            return this.hasSeriesDescription
                ? this.series.Description
                : '依課程類型與報名狀態篩選，選擇想了解的營隊方案。';
        },
        seasonTypeOptions() {
            const options = [{ text: '全部類型', value: '' }];
            const seasonOptions = this.seasonOptions;
            const values = [];
            this.allProgramList.forEach((program) => {
                const value = normalizeCampSeasonType(program.SeasonType);
                if (value && values.indexOf(value) === -1) {
                    values.push(value);
                }
            });

            seasonOptions
                .filter((item) => values.indexOf(normalizeCampSeasonType(item.value || item.Value)) !== -1)
                .forEach((item) => {
                    options.push({
                        text: item.text || item.Text,
                        value: normalizeCampSeasonType(item.value || item.Value),
                    });
                });

            return options;
        },
        gradeOptions() {
            return [
                { text: '全部', value: '' },
                { text: '小一', value: 1 },
                { text: '小二', value: 2 },
                { text: '小三', value: 3 },
                { text: '小四', value: 4 },
                { text: '小五', value: 5 },
                { text: '小六', value: 6 },
            ];
        },
    },
    watch: {
        'filters.seasonType'() {
            this.GetList();
        },
        'filters.grade'() {
            this.GetList();
        },
    },
    mounted() {
        if (!this.$route.query.id) {
            this._go('./camps');
            return;
        }

        this.id = this.$route.query.id;
        this.loadSeasonOptions();
        this.GetList();
    },
    methods: {
        loadSeasonOptions() {
            apiGetCampSeasonTypeSelector().then((res) => {
                const options = Array.isArray(res.data)
                    ? res.data
                    : (Array.isArray(res.data && res.data.Data) ? res.data.Data : []);
                this.seasonOptions = options.map((item) => ({
                    text: item.text || item.Text || item.name || item.Name,
                    value: item.value || item.Value,
                })).filter((item) => item.text && item.value);
            }).catch(() => { this.seasonOptions = []; });
        },
        GetList() {
            this.loading.programs = true;

            this.Param.Search = {};
            if (this.filters.seasonType) {
                this.Param.Search.seasonType = this.filters.seasonType;
            }
            if (this.filters.grade !== '') {
                this.Param.Search.grade = this.filters.grade;
            }
            apiGetCampSeriesProgramList(this.id, this.Param)
                .then((res) => {
                    this.series = res.data && res.data.Series ? res.data.Series : null;
                    const programs = Array.isArray(res.data && res.data.Programs)
                        ? res.data.Programs
                        : [];
                    // API 已排序時維持其結果；若舊版 API 尚未套用排序，前端仍確保招生中置頂。
                    this.programList = programs.slice().sort((a, b) =>
                        Number(Boolean(b.DisplayCanApply)) - Number(Boolean(a.DisplayCanApply))
                    );
                    if (!this.filters.seasonType && this.filters.grade === '') {
                        this.allProgramList = this.programList;
                    }
                })
                .catch(() => {
                    this.programList = [];
                })
                .finally(() => {
                    this.loading.programs = false;
                });
        },
        goProgram(program) {
            if (!program || !program.Id) {
                return;
            }

            this._go(`./campProgram?id=${program.Id}`);
        },
        fileBaseUrl() {
            return this.$store.state.FilePath;
        },
        getProgramCover(program) {
            if (!program || !program.Id || !program.Cover) {
                return './asset/no-image.png';
            }

            return this.coverUrl(program.Cover, '_camp_program', program.Id);
        },
        coverUrl(cover, folder, id) {
            if (/^https?:\/\//i.test(cover)) {
                return cover;
            }
            if (cover.startsWith('/files/')) {
                return `${this.fileBaseUrl()}${cover.slice(1)}`;
            }
            if (cover.startsWith('/')) {
                return `${this.fileBaseUrl()}files${cover}`;
            }

            return `${this.fileBaseUrl()}files/${folder}/${id}/${encodeURIComponent(cover)}`;
        },
        setDefaultCover(event) {
            event.target.src = './asset/no-image.png';
        },
        seasonText(value) {
            return getCampSeasonText(value, this.seasonOptions);
        },
        money(value) {
            return Number(value || 0).toLocaleString('zh-TW');
        },
        hasProgramPrice(program) {
            return program.DefaultProductPrice !== null && program.DefaultProductPrice !== undefined;
        },
        programDisplayPrice(program) {
            return this.isProgramEarlyBirdActive(program)
                ? program.DefaultEarlyBirdPrice
                : program.DefaultProductPrice;
        },
        isProgramEarlyBirdActive(program) {
            if (!program || program.DefaultEarlyBird !== true) return false;
            if (program.DefaultEarlyBirdPrice === null || program.DefaultEarlyBirdPrice === undefined) return false;

            const endDate = this.parseDisplayDate(program.DefaultEarlyBirdEnd);
            if (!endDate) return false;

            return endDate >= new Date();
        },
        formatDate(value) {
            const date = this.parseDisplayDate(value);
            if (!date) return '未設定';

            return `${date.getMonth() + 1}/${date.getDate()}`;
        },
        parseDisplayDate(value) {
            if (!value) return null;
            const text = String(value);
            const match = text.match(/^(\d{4})-(\d{2})-(\d{2})T?(\d{2})?:?(\d{2})?:?(\d{2})?/);
            if (match) {
                return new Date(
                    Number(match[1]),
                    Number(match[2]) - 1,
                    Number(match[3]),
                    Number(match[4] || 0),
                    Number(match[5] || 0),
                    Number(match[6] || 0),
                );
            }

            const date = new Date(text);
            return Number.isNaN(date.getTime()) ? null : date;
        },
    },
};
</script>

<style lang="scss" scoped>
.camp-programs-page {
    min-height: 100vh;
    background-color: #f7f9fb;
}

.programs-hero {
    position: relative;
    overflow: hidden;
    min-height: 250px;
    padding: 5.25rem 0 2rem;
    color: #fff;
    background-color: #087bc1;
    background-image: url("~@/assets/camp-programs-hero-blue.png");
    background-position: center;
    background-size: cover;

    h1,
    p {
        position: relative;
        z-index: 1;
    }

    h1 {
        margin-bottom: 0.75rem;
        font-size: 2.4rem;
        font-weight: 700;
        letter-spacing: 0;
        color: #fff;
    }

    p {
        margin-bottom: 0;
        font-size: 1.05rem;
        line-height: 1.8;
        color: rgba(255, 255, 255, 0.88);
    }
}

.programs-hero-overlay {
    position: absolute;
    inset: 0;
    background:
        linear-gradient(90deg, rgba(0, 58, 112, 0.34), rgba(0, 90, 170, 0.05) 68%, rgba(0, 40, 100, 0.18)),
        linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(0, 40, 100, 0.08));
}

.programs-hero-content {
    position: relative;
    z-index: 1;
    min-height: 180px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    color: #fff;
}

.programs-hero h1,
.programs-hero p {
    text-shadow: 0 3px 8px rgba(0, 38, 84, 0.42);
}

.fixed-bottom-actions {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 1.25rem;
    z-index: 1040;
    display: flex;
    justify-content: center;
    pointer-events: none;
}

.back-button {
    border-radius: 6px;
    color: #374151;
    font-weight: 700;
    border-color: rgba(31, 41, 55, 0.16);
    background-color: rgba(255, 255, 255, 0.94);
    box-shadow: 0 16px 36px rgba(31, 41, 55, 0.14);
    pointer-events: auto;
    backdrop-filter: blur(8px);
}

.section-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;

    > div {
        flex: 1 1 auto;
        min-width: 0;
    }

    h2 {
        margin-bottom: 0;
        color: #1d2d35;
        font-size: 1.55rem;
        font-weight: 700;
        letter-spacing: 0;
    }
}

.series-description {
    width: 100%;
    max-width: none;
    margin-top: 1rem;
    padding: 1.25rem 1.35rem;
    border: 1px solid rgba(31, 41, 55, 0.1);
    border-radius: 8px;
    color: #31414a;
    line-height: 1.85;
    background-color: rgba(255, 255, 255, 0.74);
    box-shadow: 0 12px 30px rgba(31, 41, 55, 0.08);
    backdrop-filter: blur(14px);
}

.series-description ::v-deep p {
    margin-bottom: 0.65rem;
}

.series-description ::v-deep p:last-child {
    margin-bottom: 0;
}

.series-description ::v-deep h1,
.series-description ::v-deep h2,
.series-description ::v-deep h3 {
    display: inline-block;
    margin: 0 0 0.85rem;
    color: #1d2d35;
    font-size: 1.45rem;
    font-weight: 800;
    line-height: 1.35;
    letter-spacing: 0;
}

.series-description ::v-deep ul,
.series-description ::v-deep ol {
    margin-bottom: 0.65rem;
    padding-left: 1.35rem;
}

.section-kicker {
    display: block;
    margin-bottom: 0.35rem;
    color: var(--primary);
    font-size: 0.85rem;
    font-weight: 700;
}

.camp-filter-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem 1.5rem;
    align-items: flex-end;
    margin-bottom: 1.5rem;
    padding: 1.15rem 1.25rem;
    border: 1px solid rgba(31, 41, 55, 0.1);
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.72);
    box-shadow: 0 12px 30px rgba(31, 41, 55, 0.08);
    backdrop-filter: blur(14px);
}

.camp-filter-bar ::v-deep .form-group {
    margin-bottom: 0;
}

.camp-filter-bar ::v-deep .camp-filter-options {
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
}

.camp-filter-bar ::v-deep .camp-filter-options .btn {
    min-width: 76px;
    height: 38px;
    padding: 0 1rem;
    border: 1px solid #dfe4ea;
    border-radius: 9px !important;
    background: #fff;
    color: #344054;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 36px;
    box-shadow: none;
    transition: background-color 0.2s ease, border-color 0.2s ease,
        color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.camp-filter-bar ::v-deep .camp-filter-options .btn:hover {
    border-color: #087bc1;
    background: #f5fbff;
    color: #087bc1;
}

.camp-filter-bar ::v-deep .camp-filter-options .btn.active {
    border-color: #087bc1;
    background: #087bc1;
    color: #fff;
    box-shadow: 0 5px 12px rgba(8, 123, 193, 0.2);
}

.camp-filter-label {
    margin-bottom: 0.45rem;
    color: #4f6068;
    font-size: 0.9rem;
    font-weight: 700;
}

.loading-block {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 240px;
}

.camp-program-card {
    height: 340px;
    overflow: hidden;
    border: 1px solid rgba(31, 41, 55, 0.1);
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 14px 34px rgba(29, 45, 53, 0.07);
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

    &:hover {
        border-color: var(--primary);
        box-shadow: 0 20px 44px rgba(0, 122, 194, 0.16);
        transform: translateY(-4px);
    }
}

.camp-program-layout {
    height: 100%;
    min-height: 0;

    > [class*="col-md-"] {
        height: 100%;
        min-width: 0;
    }

    .card-body {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        height: 100%;
        overflow: visible;

        > * {
            flex-shrink: 0;
        }

        > .d-flex {
            width: 100%;
        }
    }
}

.camp-program-layout .card-body {
    box-sizing: border-box;
}

.camp-program-cover {
    width: 100%;
    height: 100%;
    background-color: #eef3f5;
    overflow: hidden;

    .card-img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }
}

.program-title {
    display: -webkit-box;
    min-width: 0;
    overflow: hidden;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    margin-bottom: 0;
    color: #1d2d35;
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.4;
    letter-spacing: 0;
}

.camp-meta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin-bottom: 0.9rem;
}

.program-price {
    display: inline-flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 0.3rem;
    margin-bottom: 0;
    padding: 0.4rem 0.65rem;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #c43a31;
    font-size: 1.15rem;
    font-weight: 800;
}

.program-price-slot {
    display: flex;
    align-items: flex-end;
    width: 100%;
    min-height: 88px;
    margin-top: auto;
}

.program-price.early-bird {
    width: 100%;
    border-color: rgba(214, 55, 69, 0.22);
    border-left: 4px solid #d63745;
    background-color: #fff7f2;
    color: #d63745;
}

.program-price-label,
.program-price-original,
.program-price-deadline {
    font-size: 0.78rem;
    font-weight: 700;
}

.program-price-label,
.program-price-deadline {
    width: 100%;
    color: #8d4b2f;
}

.program-price-original {
    color: #7d888e;
    text-decoration: line-through;
}

.program-flags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
}

.program-flags span {
    padding: 0.25rem 0.55rem;
    border-radius: 4px;
    background-color: #e7f5f2;
    color: #08715f;
    font-size: 0.82rem;
    font-weight: 700;
}

@media (max-width: 767.98px) {
    .programs-hero {
        min-height: 230px;
        padding: 5rem 0 1.75rem;

        h1 {
            font-size: 2rem;
        }
    }

    .fixed-bottom-actions {
        bottom: 1rem;
    }

    .camp-program-card {
        height: 544px;
    }

    .camp-program-layout > .col-md-5:first-child {
        height: 220px;
    }

    .camp-program-layout > .col-md-7:last-child {
        height: 322px;
    }

    .section-heading {
        align-items: flex-start;
        flex-direction: column;
    }

    .section-heading > div {
        width: 100%;
    }

    .series-description {
        box-sizing: border-box;
        width: 100%;
        padding: 1rem;
    }

    .series-description ::v-deep h1,
    .series-description ::v-deep h2,
    .series-description ::v-deep h3 {
        font-size: 1.2rem;
    }
}
</style>
